import { doc, setDoc, getDoc, updateDoc, collection, query, where, orderBy, getDocs, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/config';

// Generate unique order ID
export const generateOrderId = () => {
    const timestamp = Date.now();
    const random = Math.floor(Math.random() * 1000);
    return `NEXA-${timestamp}-${random}`;
};

// Save order to Firestore after successful payment
export const saveOrderToFirestore = async (
    paymentData,
    userId,
    cartItems,
    shippingAddress,
    appliedCoupon = null,
    options = {}
) => {
    try {
        const orderId = generateOrderId();

        const {
            paymentMethod = 'RAZORPAY',
            paymentStatus = 'COMPLETED'
        } = options;

        // Calculate totals
        const subtotal = cartItems.reduce((total, item) => {
            let itemPrice = item.price;
            if (item.selectedRAM) itemPrice += item.selectedRAM.priceIncrease;
            if (item.selectedStorage) itemPrice += item.selectedStorage.priceIncrease;
            return total + (itemPrice * item.quantity);
        }, 0);

        // Calculate discount if coupon is applied
        let discountAmount = 0;
        if (appliedCoupon) {
            if (appliedCoupon.type === 'percentage') {
                discountAmount = (subtotal * appliedCoupon.value) / 100;
            } else {
                discountAmount = Math.min(appliedCoupon.value, subtotal);
            }
        }

        const finalAmount = Math.max(0, subtotal - discountAmount);

        const rawPayload = {
            userId: userId || 'guest',

            // Payment information
            paymentId: paymentData?.razorpay_payment_id || null,
            paymentOrderId: paymentData?.razorpay_order_id || null,
            paymentSignature: paymentData?.razorpay_signature || null,
            paymentStatus: paymentStatus,
            paymentMethod: paymentMethod,

            // Order details
            items: cartItems.map(item => ({
                productId: item.id || null,
                name: item.name || null,
                brand: item.brand || null,
                price: item.price !== undefined ? item.price : 0,
                originalPrice: item.originalPrice !== undefined ? item.originalPrice : null,
                quantity: item.quantity || 1,
                selectedRAM: item.selectedRAM,
                selectedStorage: item.selectedStorage,
                imageUrls: item.imageUrls || [],
                baseSpecs: item.baseSpecs
            })),

            // Pricing
            subtotal: subtotal,
            discount: discountAmount,
            appliedCoupon: appliedCoupon || null,
            shippingCharges: 0, // Free shipping
            tax: 0, // Tax included in price
            totalAmount: finalAmount,

            // Shipping information
            shippingAddress: shippingAddress || {},

            // Order status
            orderStatus: 'CONFIRMED',
            fulfillmentStatus: 'PROCESSING',

            // Additional metadata
            source: 'WEB',
            currency: 'INR'
        };

        // Deeply remove any undefined values using JSON stringify/parse
        const cleanPayload = JSON.parse(JSON.stringify(rawPayload));

        const orderData = {
            orderId: orderId,
            ...cleanPayload,
            // Timestamps must be added after JSON parse so they stay as FieldValue instances
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
        };

        // Save to Firestore with custom document ID
        await setDoc(doc(db, 'orders', orderId), orderData);

        return {
            success: true,
            orderId: orderId,
            orderData: orderData
        };

    } catch (error) {
        console.error('Error saving order to Firestore:', error);
        throw new Error('Failed to save order: ' + error.message);
    }
};

// Get order by ID
export const getOrderById = async (orderId) => {
    try {
        const orderDoc = await getDoc(doc(db, 'orders', orderId));
        if (orderDoc.exists()) {
            return { id: orderDoc.id, ...orderDoc.data() };
        } else {
            return null;
        }
    } catch (error) {
        console.error('Error fetching order:', error);
        throw error;
    }
};

// Get orders by user ID
export const getOrdersByUserId = async (userId) => {
    try {
        const ordersQuery = query(
            collection(db, 'orders'),
            where('userId', '==', userId),
            orderBy('createdAt', 'desc')
        );

        const querySnapshot = await getDocs(ordersQuery);
        return querySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        }));
    } catch (error) {
        console.error('Error fetching user orders:', error);
        throw error;
    }
};

// Update order status
export const updateOrderStatus = async (orderId, status, fulfillmentStatus = null) => {
    try {
        const updateData = {
            orderStatus: status,
            updatedAt: serverTimestamp()
        };

        if (fulfillmentStatus) {
            updateData.fulfillmentStatus = fulfillmentStatus;
        }

        await updateDoc(doc(db, 'orders', orderId), updateData);
        return true;
    } catch (error) {
        console.error('Error updating order status:', error);
        throw error;
    }
};

// Update order with Shiprocket metadata
export const updateOrderShiprocketDetails = async (orderId, shipmentId, shiprocketOrderId) => {
    try {
        await updateDoc(doc(db, 'orders', orderId), {
            shiprocketShipmentId: shipmentId,
            shiprocketOrderId: shiprocketOrderId,
            updatedAt: serverTimestamp()
        });
        return true;
    } catch (error) {
        console.error('Error updating Shiprocket details:', error);
        throw error;
    }
};